import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Upload, X, Zap } from 'lucide-react';
import { useCopilot, usePalette } from '@/stores/ui';
import { cn } from '@/lib/cn';

/** Floating quick dock (owner request 2026-07-17): fast upload + quick action,
 *  stacked above the AI copilot FAB. Quick action opens the ⌘K palette — Tab is
 *  reserved for keyboard navigation (accessibility), so ⌘K/Ctrl+K is the shortcut. */
export function QuickDock() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const items = [
    {
      key: 'upload',
      label: t('dock.upload'),
      icon: Upload,
      onClick: () => navigate('/documents'),
    },
    {
      key: 'quickAction',
      label: t('dock.quickAction'),
      icon: Zap,
      onClick: () => usePalette.getState().setOpen(true),
    },
  ];

  return (
    // Solo computadora: en celular los tres botones apilados tapaban el borde
    // derecho del contenido — ahí manda MobileActions (un solo botón).
    <div className="fixed bottom-20 right-6 z-[60] hidden flex-col gap-2 lg:flex">
      {items.map(({ key, label, icon: Icon, onClick }) => (
        <motion.button
          key={key}
          type="button"
          title={label}
          aria-label={label}
          onClick={onClick}
          whileTap={{ scale: 0.94 }}
          whileHover={{ y: -2, scale: 1.03 }}
          className="focus-ring glass grid h-11 w-11 place-items-center rounded-2xl border border-border/70 text-foreground shadow-glass backdrop-blur"
        >
          <Icon className="h-[18px] w-[18px]" />
        </motion.button>
      ))}
    </div>
  );
}

/** Celular (25-sep, visto en los videos demo): los 3 botones flotantes
 *  (subir, acción rápida, Loruel) ocupaban una columna de ~150 px sobre el
 *  borde derecho y tapaban botones del contenido (p. ej. «Empezar»). Ahora son
 *  UN ítem de la barra inferior («Acciones») que despliega las 3 acciones CON
 *  su nombre sobre la barra — nada flota encima del contenido. */
export function MobileActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const copilotOpen = useCopilot((s) => s.open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  const items = [
    { key: 'assistant', label: t('dock.assistant'), icon: Sparkles, onClick: run(() => useCopilot.getState().setOpen(true)) },
    { key: 'upload', label: t('dock.uploadMobile'), icon: Upload, onClick: run(() => navigate('/documents')) },
    { key: 'quickAction', label: t('dock.quickActionMobile'), icon: Zap, onClick: run(() => usePalette.getState().setOpen(true)) },
  ];
  const active = open || copilotOpen;

  // Portal a <body>: la barra usa backdrop-filter (crea contenedor para los
  // `fixed` hijos) — sin portal, el velo solo cubría la barra.
  const overlay = createPortal(
    <>
      {open && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[45] bg-background/60 backdrop-blur-[2px] lg:hidden"
        />
      )}
      <AnimatePresence>
        {open && (
          <motion.ul
            id="mobile-actions"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="fixed bottom-[calc(5.75rem+env(safe-area-inset-bottom))] right-4 z-[46] flex flex-col items-end gap-2 lg:hidden"
          >
            {items.map(({ key, label, icon: Icon, onClick }) => (
              <li key={key}>
                <button
                  type="button"
                  onClick={onClick}
                  className="focus-ring glass-strong flex items-center gap-2.5 rounded-full border border-border/70 py-2.5 pl-3.5 pr-4 text-sm font-semibold text-foreground shadow-glass"
                >
                  <Icon className="h-4 w-4 text-primary" />
                  {label}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </>,
    document.body,
  );

  return (
    <>
      {overlay}
      <button
        type="button"
        aria-label={copilotOpen ? t('dock.closeAssistant') : t('dock.menu')}
        aria-expanded={open}
        aria-controls="mobile-actions"
        onClick={() => (copilotOpen ? useCopilot.getState().setOpen(false) : setOpen((o) => !o))}
        className={cn(
          'focus-ring flex flex-1 flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-[10px] transition-colors',
          active ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        <span className="grid h-5 w-5 place-items-center rounded-md bg-brand-gradient text-white">
          {active ? <X className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
        </span>
        <span className="truncate">{t('dock.menuShort')}</span>
      </button>
    </>
  );
}
